// src/components/registro/SelectorMunicipio.js
// (Portado de la web HolaVeci para el registro de HolaVeci Local, D-23.)
//
// Cascada País → Departamento → Municipio contra los endpoints
// públicos de catálogo geográfico.
//
// Devuelve al padre municipioId (int) por onChange.

import React, { useEffect, useState } from 'react';
import { TextField, MenuItem, Grid, Alert } from '@mui/material';
import { registroLocal as geo } from '../../api/holaveciLocalApi';

export default function SelectorMunicipio({ value, onChange, disabled, onSeleccion }) {
  const [paises, setPaises]                   = useState([]);
  const [departamentos, setDepartamentos]     = useState([]);
  const [municipios, setMunicipios]           = useState([]);
  const [paisId, setPaisId]                   = useState('');
  const [departamentoId, setDepartamentoId]   = useState('');
  const [cargando, setCargando]               = useState(false);
  const [error, setError]                     = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await geo.paises();
        const lista = Array.isArray(r) ? r : (r.items || []);
        setPaises(lista);
        // Colombia por default si aparece. PaisDto (CatalogoController,
        // que es el que consume este componente) usa "Id" simple para
        // todos los niveles — no "PaisID"/"DepartamentoID"/etc como el
        // DTO homónimo de /api/pos/*. Ver idOf() más abajo.
        const co = lista.find(p => p.iso2 === 'CO');
        if (co) setPaisId(idOf(co));
      } catch { setError('No se pudieron cargar los países.'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!paisId) { setDepartamentos([]); return; }
    (async () => {
      setCargando(true);
      try {
        const r = await geo.departamentos(paisId);
        setDepartamentos(Array.isArray(r) ? r : (r.items || []));
      } catch { setError('No se pudieron cargar los departamentos.'); }
      finally { setCargando(false); }
    })();
  }, [paisId]);

  useEffect(() => {
    if (!departamentoId) { setMunicipios([]); return; }
    (async () => {
      setCargando(true);
      try {
        const r = await geo.municipios(departamentoId);
        setMunicipios(Array.isArray(r) ? r : (r.items || []));
      } catch { setError('No se pudieron cargar los municipios.'); }
      finally { setCargando(false); }
    })();
  }, [departamentoId]);

  // PaisDto/DepartamentoDto/MunicipioDto (CatalogoController) usan
  // simplemente "Id" en los tres niveles — no "PaisID"/"DepartamentoID"/
  // "MunicipioID" como los DTOs homónimos de /api/pos/*, que es otro
  // controller distinto que este componente NO consume.
  const idOf = (row, keys = ['id', 'Id']) => {
    for (const k of keys) if (row?.[k] !== undefined) return row[k];
    return '';
  };
  const nameOf = (row) => row?.nombre || row?.Nombre || '';

  // Además del id (onChange, para guardar en el form), el padre puede
  // pedir el nombre completo resuelto vía onSeleccion — lo necesita
  // MapaPinDraggable para meter "Floridablanca, Santander" en el texto
  // de búsqueda del geocoder. Un simple sesgo de coordenadas (bounds)
  // no es suficiente: Google puede ignorarlo si encuentra en otra
  // ciudad un formato de dirección que le resulta más "seguro" (así
  // fue como "Calle 195 # 80-16" terminó en Bogotá estando en
  // Floridablanca — 195 es una numeración típica bogotana).
  useEffect(() => {
    if (!onSeleccion) return;
    const m = municipios.find(x => String(idOf(x)) === String(value));
    if (!m) return;
    const d = departamentos.find(x => String(idOf(x)) === String(departamentoId));
    onSeleccion({
      municipioNombre:    nameOf(m),
      departamentoNombre: d ? nameOf(d) : '',
      lat: m.latitud ?? m.Latitud ?? null,
      lng: m.longitud ?? m.Longitud ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, municipios, departamentos, departamentoId]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            select fullWidth label="País" value={paisId || ''}
            onChange={(e) => { setPaisId(e.target.value); setDepartamentoId(''); onChange(''); }}
            disabled={disabled}
          >
            {paises.map(p => (
              <MenuItem key={idOf(p)} value={idOf(p)}>{nameOf(p)}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select fullWidth label="Departamento" value={departamentoId || ''}
            onChange={(e) => { setDepartamentoId(e.target.value); onChange(''); }}
            disabled={disabled || !paisId || cargando}
          >
            {departamentos.map(d => (
              <MenuItem key={idOf(d)} value={idOf(d)}>
                {nameOf(d)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select fullWidth label="Municipio" value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || !departamentoId || cargando}
          >
            {municipios.map(m => (
              <MenuItem key={idOf(m)} value={idOf(m)}>
                {nameOf(m)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
    </>
  );
}
